import { FormEvent, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { expenseService } from "@/api/expense.service";
import { useAuth } from "@/context/AuthContext";
import type { HouseholdExpenses, RecurringExpense, OccasionalExpense, ParsedOfxTransaction } from "@/api/types";
import { CATEGORIES } from "@/api/categories";
import OfxImportDialog from "@/components/OfxImportDialog";

const MONTHS_FULL = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const fmt = (n: number) => n.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

const now = new Date();
const MONTH_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
  return { value: `${d.getFullYear()}-${d.getMonth() + 1}`, month: d.getMonth() + 1, year: d.getFullYear(), label: `${MONTHS_FULL[d.getMonth()]} ${d.getFullYear()}` };
});

function CategoryChip({ category }: { category: string }) {
  const cat = CATEGORIES.find(c => c.value === category) ?? CATEGORIES[CATEGORIES.length - 1];
  return <Chip label={cat.label} size="small" sx={{ bgcolor: cat.color + "22", color: cat.color, fontWeight: 600, fontSize: "0.7rem" }} />;
}

type EditingExpense = { id: string; type: "recurring" | "occasional" };

export default function ExpensesPage() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<EditingExpense | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [sharedWith, setSharedWith] = useState(false);
  const [category, setCategory] = useState("other");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [ofxTransactions, setOfxTransactions] = useState<ParsedOfxTransaction[] | null>(null);
  const [importSuccess, setImportSuccess] = useState("");

  // Household-wide expenses + filters
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [selectedMonthKey, setSelectedMonthKey] = useState("all");
  const [selectedShared, setSelectedShared] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [data, setData] = useState<HouseholdExpenses | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const selectedMonth = MONTH_OPTIONS.find(o => o.value === selectedMonthKey);

  const loadExpenses = async () => {
    setListLoading(true); setListError("");
    try {
      const params = selectedMonth
        ? { userId: selectedUserId, month: selectedMonth.month, year: selectedMonth.year }
        : { userId: selectedUserId, month: "all" as const };
      setData(await expenseService.getAll(params));
    } catch (e: any) {
      // Not (yet) part of a household: fall back to a solo view of my own expenses.
      if (user && String(e.response?.data?.error || "").includes("foyer")) {
        setData({
          members: [{ id: user.id, name: user.name }],
          recurring: user.recurringExpenses,
          occasional: selectedMonth
            ? user.occasionalExpenses.filter(x => x.month === selectedMonth.month && x.year === selectedMonth.year)
            : user.occasionalExpenses,
        });
      } else {
        setListError(e.response?.data?.error || "Erreur.");
      }
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadExpenses(); }, [selectedUserId, selectedMonthKey, user]);

  const matchesFilters = (e: RecurringExpense | OccasionalExpense) =>
    (selectedShared === "all" || (selectedShared === "shared" ? e.sharedWith : !e.sharedWith)) &&
    (selectedCategory === "all" || e.category === selectedCategory);

  const recurring = (data?.recurring || []).filter(matchesFilters);
  const occasional = (data?.occasional || []).filter(matchesFilters);
  const members = data?.members || [];

  const ownerLabel = (e: RecurringExpense | OccasionalExpense) => e.userId === user?.id ? "Moi" : (e.userName || "");
  const canEdit = (e: RecurringExpense | OccasionalExpense) => !e.userId || e.userId === user?.id;

  const occasionalGroups = (() => {
    const sorted = [...occasional].sort((a, b) => b.year !== a.year ? b.year - a.year : b.month !== a.month ? b.month - a.month : new Date(b.date).getTime() - new Date(a.date).getTime());
    const groups: { key: string; label: string; total: number; expenses: OccasionalExpense[] }[] = [];
    for (const e of sorted) {
      const key = `${e.year}-${e.month}`;
      let g = groups.find(g => g.key === key);
      if (!g) { g = { key, label: `${MONTHS_FULL[e.month - 1]} ${e.year}`, total: 0, expenses: [] }; groups.push(g); }
      g.expenses.push(e);
      g.total += e.amount;
    }
    return groups;
  })();

  const openAdd = () => {
    setEditing(null);
    setLabel(""); setAmount(""); setDate(new Date().toISOString().split("T")[0]);
    setSharedWith(false); setCategory("other");
    setError("");
    setOpen(true);
  };

  const openEdit = (e: RecurringExpense | OccasionalExpense, type: "recurring" | "occasional") => {
    setEditing({ id: e.id, type });
    setLabel(e.label);
    setAmount(String(e.amount));
    setSharedWith(e.sharedWith);
    setCategory(e.category);
    if (type === "occasional") setDate(new Date((e as OccasionalExpense).date).toISOString().split("T")[0]);
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      if (editing) {
        if (editing.type === "recurring") await expenseService.updateRecurring(editing.id, { label, amount: parseFloat(amount), sharedWith, category });
        else await expenseService.updateOccasional(editing.id, { label, amount: parseFloat(amount), date, sharedWith, category });
      } else {
        if (tab === 0) await expenseService.addRecurring({ label, amount: parseFloat(amount), sharedWith, category });
        else await expenseService.addOccasional({ label, amount: parseFloat(amount), date, sharedWith, category });
      }
      await Promise.all([refreshUser(), loadExpenses()]);
      setOpen(false);
    } catch (e: any) { setError(e.response?.data?.error || "Erreur."); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: string, type: "recurring" | "occasional") => {
    if (type === "recurring") await expenseService.deleteRecurring(id);
    else await expenseService.deleteOccasional(id);
    await Promise.all([refreshUser(), loadExpenses()]);
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true); setImportError("");
    try {
      const transactions = await expenseService.parseOfx(file);
      setOfxTransactions(transactions);
    } catch (err: any) { setImportError(err.response?.data?.error || "Impossible de lire ce fichier OFX."); }
    finally { setImporting(false); }
  };

  const handleImported = async (result: { imported: number; skipped: number }) => {
    await Promise.all([refreshUser(), loadExpenses()]);
    setOfxTransactions(null);
    setImportSuccess(`${result.imported} dépense${result.imported > 1 ? "s" : ""} importée${result.imported > 1 ? "s" : ""}${result.skipped ? ` (${result.skipped} déjà importée${result.skipped > 1 ? "s" : ""})` : ""}.`);
  };

  const isOccasionalEdit = editing?.type === "occasional";
  const dialogTab = editing ? (editing.type === "recurring" ? 0 : 1) : tab;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: "auto" }}>
      <Paper elevation={1} sx={{ borderRadius: 3 }}>
        <Box sx={{ px: 3, pt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" fontWeight={600}>Dépenses du foyer</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <input ref={fileInputRef} type="file" accept=".ofx" hidden onChange={handleFileSelected} />
            <Button
              startIcon={importing ? <CircularProgress size={16} /> : <UploadFileIcon />}
              variant="outlined"
              size="small"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
            >
              Importer OFX
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openAdd}>Ajouter</Button>
          </Box>
        </Box>

        <Box sx={{ px: 3, pt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Membre</InputLabel>
            <Select value={selectedUserId} label="Membre" onChange={e => setSelectedUserId(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {members.map(m => <MenuItem key={m.id} value={m.id}>{m.id === user?.id ? "Moi" : m.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Mois</InputLabel>
            <Select value={selectedMonthKey} label="Mois" onChange={e => setSelectedMonthKey(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              {MONTH_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Partage</InputLabel>
            <Select value={selectedShared} label="Partage" onChange={e => setSelectedShared(e.target.value)}>
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="shared">Partagées</MenuItem>
              <MenuItem value="personal">Personnelles</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Catégorie</InputLabel>
            <Select value={selectedCategory} label="Catégorie" onChange={e => setSelectedCategory(e.target.value)}>
              <MenuItem value="all">Toutes</MenuItem>
              {CATEGORIES.map(c => (
                <MenuItem key={c.value} value={c.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />
                    {c.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {importError && <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setImportError("")}>{importError}</Alert>}
        {listError && <Alert severity="error" sx={{ mx: 3, mt: 2 }}>{listError}</Alert>}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, mt: 1 }}>
          <Tab label={`Récurrentes (${recurring.length})`} />
          <Tab label={`Ponctuelles (${occasional.length})`} />
        </Tabs>
        <Divider />
        {listLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
        ) : (
        <List>
          {tab === 0 && recurring.length === 0 && <ListItem><ListItemText secondary="Aucune dépense" /></ListItem>}
          {tab === 0 && recurring.map(e => (
            <ListItem key={e.id} secondaryAction={canEdit(e) && (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton edge="end" size="small" onClick={() => openEdit(e, "recurring")}><EditIcon fontSize="small" /></IconButton>
                <IconButton edge="end" size="small" onClick={() => handleDelete(e.id, "recurring")} color="error"><DeleteIcon fontSize="small" /></IconButton>
              </Box>
            )}>
              <ListItemText
                primary={<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  {e.label}
                  <CategoryChip category={e.category} />
                  {e.sharedWith && <Chip label="Partagé" size="small" color="primary" variant="outlined" />}
                  {selectedUserId === "all" && <Chip label={ownerLabel(e)} size="small" variant="outlined" />}
                </Box>}
                secondary={fmt(e.amount) + " / mois"}
              />
            </ListItem>
          ))}
          {tab === 1 && occasionalGroups.length === 0 && <ListItem><ListItemText secondary="Aucune dépense" /></ListItem>}
          {tab === 1 && occasionalGroups.map(group => [
            <ListSubheader key={group.key} sx={{ bgcolor: "action.hover", borderRadius: 1, minHeight: 40, lineHeight: "normal", display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, my: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>{group.label}</Typography>
              <Typography variant="caption" color="text.secondary">{fmt(group.total)}</Typography>
            </ListSubheader>,
            ...group.expenses.map(e => (
              <ListItem key={e.id} secondaryAction={canEdit(e) && (
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton edge="end" size="small" onClick={() => openEdit(e, "occasional")}><EditIcon fontSize="small" /></IconButton>
                  <IconButton edge="end" size="small" onClick={() => handleDelete(e.id, "occasional")} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              )}>
                <ListItemText
                  primary={<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                    {e.label}
                    <CategoryChip category={e.category} />
                    {e.sharedWith && <Chip label="Partagé" size="small" color="primary" variant="outlined" />}
                    {selectedUserId === "all" && <Chip label={ownerLabel(e)} size="small" variant="outlined" />}
                  </Box>}
                  secondary={`${fmt(e.amount)} — ${new Date(e.date).toLocaleDateString("fr-FR")}`}
                />
              </ListItem>
            )),
          ])}
        </List>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? "Modifier la dépense" : `Ajouter une dépense ${dialogTab === 0 ? "récurrente" : "ponctuelle"}`}</DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="Libellé" value={label} onChange={e => setLabel(e.target.value)} required fullWidth />
            <TextField label="Montant (€)" type="number" value={amount} onChange={e => setAmount(e.target.value)} required fullWidth inputProps={{ min: 0, step: 0.01 }} />
            {(dialogTab === 1 || isOccasionalEdit) && <TextField label="Date" type="date" value={date} onChange={e => setDate(e.target.value)} required fullWidth InputLabelProps={{ shrink: true }} />}
            <FormControl fullWidth>
              <InputLabel>Catégorie</InputLabel>
              <Select value={category} label="Catégorie" onChange={e => setCategory(e.target.value)}>
                {CATEGORIES.map(c => (
                  <MenuItem key={c.value} value={c.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: c.color, flexShrink: 0 }} />
                      {c.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel control={<Switch checked={sharedWith} onChange={e => setSharedWith(e.target.checked)} />} label="Dépense partagée" />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setOpen(false)}>Annuler</Button>
            <Button type="submit" variant="contained" disabled={loading}>{loading ? <CircularProgress size={20} /> : editing ? "Enregistrer" : "Ajouter"}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <OfxImportDialog
        open={ofxTransactions !== null}
        transactions={ofxTransactions || []}
        onClose={() => setOfxTransactions(null)}
        onImported={handleImported}
      />

      <Snackbar
        open={!!importSuccess}
        autoHideDuration={4000}
        onClose={() => setImportSuccess("")}
        message={importSuccess}
      />
    </Box>
  );
}
